import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../../../utils/functions/api';
import { showAlert, confirmAsync } from '../../../utils/functions/globalDialog';
import EmployeeAutocomplete from './EmployeeAutocomplete';
import { monthLabel } from './utils';

const emptyFormA = { tax_id: '', title: '', firstname: '', lastname: '', salary: '', sso: '' };
const emptyFormB = { employee: null, salary: '', sso: '' };

// row มาจาก ssoMonthlyAddition.service.js#toRow (flatten แล้ว) — title/firstname/lastname เป็น
// string ตรงๆ ไม่ต้องเช็ค row.employee.firstname_th แบบ ExternalPersonModal.js เพราะ backend
// resolve ชื่อจริง (ถ้าผูก employee) หรือใช้ค่าที่กรอกเอง (ถ้าเป็นคนนอกจริง) ให้แล้ว
function displayName(row) {
  return `${row.title || ''}${row.firstname} ${row.lastname}`.trim();
}

// เพิ่มคนเฉพาะเดือนที่เปิด modal นี้อยู่เท่านั้น (ต่างจาก ExternalPersonModal "คนนอก" ที่ขึ้นซ้ำทุก
// เดือน) — รายชื่อที่เพิ่มจะไปโผล่ทั้งในหน้า "รายละเอียด" (ตารางแยกต่างหาก) และไฟล์ส่งออก ปกส. ของ
// เดือนนั้นเลย (ดู getMonthDetail/getSsoExportList ฝั่ง backend) ฟอร์ม/โครง UI เลียนแบบ
// ExternalPersonModal.js เกือบทั้งหมด ต่างกันแค่ผูก year/month และไม่มี toggle "แสดงในไฟล์"
// (ลบตรงๆ ได้เลยเพราะ scope แค่เดือนเดียวอยู่แล้ว ไม่ต้องปิดชั่วคราว)
export default function MonthlyAdditionModal({ open, onClose, year, month }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [tab, setTab] = useState(0); // 0 = เลือกจาก employee (Type B), 1 = คนที่ไม่มีชื่อในพนักงาน (Type A)
  const [formA, setFormA] = useState(emptyFormA);
  const [formB, setFormB] = useState(emptyFormB);
  const [saving, setSaving] = useState(false);

  const baseUrl = `${process.env.REACT_APP_API_URL}/monthly-salary-summary/${year}/${month}/additions`;

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(baseUrl);
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, year, month]);

  const resetAddForm = () => {
    setTab(0);
    setFormA(emptyFormA);
    setFormB(emptyFormB);
  };

  const handleFieldEdit = async (row, field, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return;
    }
    await api.put(`${baseUrl}/${row.id}`, { [field]: num });
    fetchRows();
  };

  const handleDelete = async (row) => {
    if (!(await confirmAsync(`ลบ "${displayName(row)}" ออกจากรายชื่อที่เพิ่มเฉพาะเดือนนี้หรือไม่?`))) {
      return;
    }
    await api.delete(`${baseUrl}/${row.id}`);
    fetchRows();
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = tab === 1
        ? { ...formA, salary: Number(formA.salary), sso: Number(formA.sso) }
        : { employee: formB.employee?._id, salary: Number(formB.salary), sso: Number(formB.sso) };

      if (tab === 0 && !payload.employee) {
        showAlert('กรุณาเลือกพนักงาน');
        return;
      }
      if (!Number.isFinite(payload.salary) || !Number.isFinite(payload.sso)) {
        showAlert('กรุณาระบุค่าจ้างเดือนและเงินสมทบให้ถูกต้อง');
        return;
      }

      await api.post(baseUrl, payload);
      showAlert('เพิ่มรายชื่อสำเร็จ');
      resetAddForm();
      setAddOpen(false);
      fetchRows();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>{`เพิ่มคนเฉพาะเดือนนี้ — ${monthLabel(year, month)}`}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          รายชื่อในนี้จะขึ้นเฉพาะเดือน {monthLabel(year, month)} เท่านั้น (ทั้งในหน้ารายละเอียดและไฟล์ส่งออก ปกส.) ไม่ขึ้นซ้ำเดือนอื่นเหมือน &quot;คนนอก&quot;
        </Typography>

        {!addOpen ? (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ mb: 2 }}>
            เพิ่มคน
          </Button>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="เลือกจากพนักงานในระบบ" />
              <Tab label="ไม่มีชื่อในระบบพนักงาน" />
            </Tabs>

            {tab === 0 ? (
              <Stack spacing={2}>
                <EmployeeAutocomplete
                  value={formB.employee}
                  onChange={(v) => setFormB((p) => ({ ...p, employee: v }))}
                  placeholder="ค้นหาชื่อพนักงาน..."
                />
                <Typography variant="caption" color="text.secondary">
                  เลขบัตรประชาชน / คำนำหน้า / ชื่อ / สกุล จะดึงจากข้อมูลพนักงานอัตโนมัติ กรอกแค่ค่าจ้างเดือน/เงินสมทบ
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="ค่าจ้างเดือน"
                    size="small"
                    fullWidth
                    type="number"
                    value={formB.salary}
                    onChange={(e) => setFormB((p) => ({ ...p, salary: e.target.value }))}
                  />
                  <TextField
                    label="เงินสมทบ"
                    size="small"
                    fullWidth
                    type="number"
                    value={formB.sso}
                    onChange={(e) => setFormB((p) => ({ ...p, sso: e.target.value }))}
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="เลขบัตรประชาชน"
                    size="small"
                    fullWidth
                    value={formA.tax_id}
                    onChange={(e) => setFormA((p) => ({ ...p, tax_id: e.target.value }))}
                  />
                  <TextField
                    select
                    label="คำนำหน้า"
                    size="small"
                    sx={{ minWidth: 140 }}
                    value={formA.title}
                    onChange={(e) => setFormA((p) => ({ ...p, title: e.target.value }))}
                  >
                    <MenuItem value="นาย">นาย</MenuItem>
                    <MenuItem value="นาง">นาง</MenuItem>
                    <MenuItem value="นางสาว">นางสาว</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="ชื่อ"
                    size="small"
                    fullWidth
                    value={formA.firstname}
                    onChange={(e) => setFormA((p) => ({ ...p, firstname: e.target.value }))}
                  />
                  <TextField
                    label="สกุล"
                    size="small"
                    fullWidth
                    value={formA.lastname}
                    onChange={(e) => setFormA((p) => ({ ...p, lastname: e.target.value }))}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="ค่าจ้างเดือน"
                    size="small"
                    fullWidth
                    type="number"
                    value={formA.salary}
                    onChange={(e) => setFormA((p) => ({ ...p, salary: e.target.value }))}
                  />
                  <TextField
                    label="เงินสมทบ"
                    size="small"
                    fullWidth
                    type="number"
                    value={formA.sso}
                    onChange={(e) => setFormA((p) => ({ ...p, sso: e.target.value }))}
                  />
                </Stack>
              </Stack>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={() => { resetAddForm(); setAddOpen(false); }}>ยกเลิก</Button>
              <Button variant="contained" onClick={handleCreate} disabled={saving}>
                {saving ? 'กำลังบันทึก...' : 'สร้าง'}
              </Button>
            </Stack>
          </Paper>
        )}

        {loading ? (
          <Skeleton variant="rounded" width="100%" height={200} animation="wave" />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ชื่อ</TableCell>
                  <TableCell>ประเภท</TableCell>
                  <TableCell align="right">ค่าจ้างเดือน</TableCell>
                  <TableCell align="right">เงินสมทบ</TableCell>
                  <TableCell align="center">ลบ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 3, color: 'text.secondary' }}>ยังไม่มีคนที่เพิ่มไว้เฉพาะเดือนนี้</Box>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{displayName(row)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.employee ? 'พนักงานในระบบ' : 'ไม่มีในระบบ'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard"
                        type="number"
                        size="small"
                        defaultValue={row.salary}
                        onBlur={(e) => handleFieldEdit(row, 'salary', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard"
                        type="number"
                        size="small"
                        defaultValue={row.sso}
                        onBlur={(e) => handleFieldEdit(row, 'sso', e.target.value)}
                        sx={{ width: 80 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
